import React from "react";
import { useCreatePromptTemplateMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { LegacyTokens } from "../../../theme/b13Tokens";
import PromptLevelModal from "../../Settings/PromptTemplates/shared/PromptLevelModal";
import PromptLevelEditorBody, {
  EditorLevel,
  makeEmptyLevel,
  normaliseLevel,
} from "../../Settings/PromptTemplates/shared/PromptLevelEditorBody";
import { OutcomeMode, DEFAULT_OUTCOME_MODE } from "../../Settings/PromptTemplates/shared/outcomeModeConstants";

// Batch 6 — replacement for the "hierarchy" branch of QuickHierarchyDrawer.
//
// Uses the same PromptLevelModal + PromptLevelEditorBody as the Settings
// route so the editing surface is visually identical no matter where the
// user opens it. Workflow branch continues to render the legacy drawer
// (Batch 7 will replace it with an equivalent WorkflowModal).
//
// Removed vs the drawer:
//   - `Your Program draft will remain open.` subtitle (§B)
//   - `Save Draft` button (§B)
//   - `Cannot save & activate: …` floating error (§C)
//   - Prompting Ladder preview panel (D2 decision)

// Standard seven-level hierarchy, matches the settings-editor preset.
const STARTER_LEVELS: Array<Omit<EditorLevel, "orderNumber" | "countsAsIndependent">> = [
  { levelName: "Full Physical",    shortCode: "FP", description: "Hand-over-hand support through the full response.", score: 10,  weightedScore: null, colour: LegacyTokens.red,     outcomeState: "not_counted",    isActive: true },
  { levelName: "Partial Physical", shortCode: "PP", description: "Physical support at the elbow / shoulder only.",     score: 25,  weightedScore: null, colour: LegacyTokens.clay,    outcomeState: "not_counted",    isActive: true },
  { levelName: "Model",            shortCode: "M",  description: "Demonstrate the response for the learner to imitate.", score: 40,  weightedScore: null, colour: LegacyTokens.amber,   outcomeState: "not_counted",    isActive: true },
  { levelName: "Gestural",         shortCode: "G",  description: "Point / gesture toward the correct response.",       score: 55,  weightedScore: null, colour: LegacyTokens.teal,    outcomeState: "not_counted",    isActive: true },
  { levelName: "Verbal",           shortCode: "V",  description: "Verbal cue that names or hints at the response.",    score: 70,  weightedScore: null, colour: LegacyTokens.blue,    outcomeState: "not_counted",    isActive: true },
  { levelName: "Visual",           shortCode: "Vi", description: "Visual cue (image, text, arrow, model).",            score: 85,  weightedScore: null, colour: LegacyTokens.purple,  outcomeState: "not_counted",    isActive: true },
  { levelName: "Independent",      shortCode: "I",  description: "Learner responds without any prompt.",               score: 100, weightedScore: null, colour: LegacyTokens.emerald, outcomeState: "successful", isActive: true },
];

const starterLevels = (): EditorLevel[] =>
  STARTER_LEVELS.map((l, i) =>
    normaliseLevel({ ...l, orderNumber: i, countsAsIndependent: l.outcomeState === "successful" }, i)
  );

export default function HierarchyQuickModal(props: {
  open: boolean;
  onClose: () => void;
  onCreated: (kind: "hierarchy" | "workflow", id: number) => void;
}) {
  const { open, onClose, onCreated } = props;
  const { showMessage, showErrorMessage } = useNotifier();
  const [createHierarchy, { isLoading: saving }] = useCreatePromptTemplateMutation();

  const [templateName, setTemplateName] = React.useState("");
  const [description, setDescription]   = React.useState("");
  const [direction, setDirection]       = React.useState("most_to_least");
  const [isDefault, setIsDefault]       = React.useState(false);
  const [outcomeMode, setOutcomeMode]   = React.useState<OutcomeMode>(DEFAULT_OUTCOME_MODE);
  const [levels, setLevels]             = React.useState<EditorLevel[]>(starterLevels);
  const [dirty, setDirty]               = React.useState(false);
  const [validated, setValidated]       = React.useState(false);

  const templateNameRef = React.useRef<HTMLInputElement | null>(null);
  const levelNameRefs   = React.useRef<Record<number, HTMLInputElement | null>>({});

  // Reset when the modal is (re)opened so a second open doesn't inherit
  // the previous session's edits.
  React.useEffect(() => {
    if (open) {
      setTemplateName("");
      setDescription("");
      setDirection("most_to_least");
      setIsDefault(false);
      setOutcomeMode(DEFAULT_OUTCOME_MODE);
      setLevels(starterLevels());
      setDirty(false);
      setValidated(false);
    }
  }, [open]);

  const templateNameError = React.useMemo<string | null>(() => {
    if (!validated) return null;
    if (!templateName.trim()) return "Please enter a name for this template.";
    return null;
  }, [templateName, validated]);

  const levelErrors: Record<number, string | null> = React.useMemo(() => {
    if (!validated) return {};
    const out: Record<number, string | null> = {};
    levels.forEach((lv, i) => {
      if (!lv.levelName.trim()) out[i] = "Level name is required.";
    });
    const seen = new Map<string, number>();
    levels.forEach((lv, i) => {
      const key = lv.levelName.trim().toLowerCase();
      if (!key) return;
      if (seen.has(key)) out[i] = "Duplicate level name.";
      else seen.set(key, i);
    });
    return out;
  }, [levels, validated]);

  const disableSave = validated && (Boolean(templateNameError) || Object.values(levelErrors).some(Boolean));

  const focusFirstInvalid = () => {
    if (!templateName.trim()) {
      templateNameRef.current?.focus();
      templateNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const firstBad = levels.findIndex((l) => !l.levelName.trim());
    if (firstBad >= 0) {
      const el = levelNameRefs.current[firstBad];
      el?.focus();
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const save = async () => {
    setValidated(true);
    if (!templateName.trim() || levels.some((l) => !l.levelName.trim())) {
      setTimeout(focusFirstInvalid, 0);
      return;
    }
    try {
      const res: any = await createHierarchy({
        templateName: templateName.trim(),
        description: description || null,
        direction,
        isDefault,
        status: "active",
        outcomeMode,
        levels: levels
          .filter((l) => l.levelName.trim())
          .map((l, i) => ({
            levelName: l.levelName.trim(),
            shortCode: l.shortCode || null,
            description: l.description || null,
            score: l.score,
            weightedScore: l.weightedScore === null ? null : l.weightedScore,
            colour: l.colour,
            countsAsIndependent: l.outcomeState === "successful",
            outcomeState: l.outcomeState,
            isActive: l.isActive,
            orderNumber: i,
          })),
      }).unwrap();
      if (res?.status && res?.data?.id) {
        showMessage("Prompt Levels saved.");
        setDirty(false);
        onCreated("hierarchy", Number(res.data.id));
        onClose();
      } else {
        showErrorMessage(res?.message || "Failed to save prompt level template.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Save failed.");
    }
  };

  return (
    <PromptLevelModal
      open={open}
      title="Add prompt level template"
      onClose={onClose}
      onSave={save}
      onOpenInNewTab={() => window.open("/aba-therapy/prompt-hierarchies/new", "_blank", "noopener")}
      saving={saving}
      disableSave={disableSave}
      hasUnsavedChanges={dirty}
    >
      <PromptLevelEditorBody
        templateName={templateName}
        onTemplateNameChange={(v) => { setDirty(true); setTemplateName(v); }}
        templateNameError={templateNameError}
        description={description}
        onDescriptionChange={(v) => { setDirty(true); setDescription(v); }}
        isDefault={isDefault}
        onDefaultChange={(v) => { setDirty(true); setIsDefault(v); }}
        direction={direction}
        onDirectionChange={(v) => { setDirty(true); setDirection(v); }}
        onReverse={() => {
          setDirty(true);
          setLevels((L) => [...L].reverse().map((l, i) => ({ ...l, orderNumber: i })));
        }}
        outcomeMode={outcomeMode}
        onOutcomeModeChange={(m) => { setDirty(true); setOutcomeMode(m); }}
        levels={levels}
        onLevelsChange={(u) => { setDirty(true); setLevels(u); }}
        levelErrors={levelErrors}
        registerNameRef={(el) => { templateNameRef.current = el; }}
        registerLevelNameRef={(i, el) => { levelNameRefs.current[i] = el; }}
      />
    </PromptLevelModal>
  );
}
